import styles from "./MyProfile.module.scss";
import { Typography, Box, Button, Grid, OutlinedInput } from "@mui/material";
import QRCode from "qrcode";
import ProfileCard from "components/ProfileCard/ProfileCard";
import { useState, useEffect, Fragment } from "react";
import { useAppDispatch, useAppSelector } from "utils/hooks/storeHooks";
import Edit from "components/icons/Edit";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { Form, Formik } from "formik";
import {
  ProfileModel,
  ProfileFormValues,
  ProfileDefaultFilters,
} from "types/Profile";
import {
  getFilterRolePermission,
  getProfileById,
  updateProfile,
} from "store/profile/thunks";
import { SelectOption } from "types";
import { EditProfileSchema } from "./validators/editProfileSchema";
import {
  selectProfileFilterRole,
  selectProfileUpdateStatus,
  selectTaxonomyOptionsByRole,
} from "store/profile/selectors";
import {
  setProfileUpdateStatus,
  setTaxonomyOptionsByRole,
  setTaxonomyOptionsByRoleRequestStatus,
} from "store/profile";
import {
  selectOtpAuthUrl,
  selectSessionCredentials,
  selectTwoFAUpdateStatus,
} from "store/session/selectors";
import {
  disable2FA,
  getOtpAuthUrl,
  verifyOtpAndActivate2FA,
} from "store/session/thunks";
import { setTwoFAUpdateStatus } from "store/session";
import { getSelectOptions } from "utils/helpers/selectOptionsHelper";
import { RequestStatus as Status } from "constants/RequestStatus";
import Modal from "components/Modal/Modal";
import variables from "../../styles/partials/_variables.module.scss";
import Toast from "components/Toast/Toast";
import { ProfileToastMessages } from "constants/Profile";

const MyProfile = () => {
  const dispatch = useAppDispatch();
  const credentials = useAppSelector(selectSessionCredentials);
  const isTwoFAEnabled = credentials?.user?.otpVerified;
  const twoFAUpdateStatus = useAppSelector(selectTwoFAUpdateStatus);
  const otpAuthUrl = useAppSelector(selectOtpAuthUrl);
  const { user, profile }: { user: any; profile: ProfileModel } = credentials;
  const [isEditing, setIsEditing] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isActionBtnDisabled, setIsActionBtnDisabled] = useState<boolean>(true);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [sixDigitCode, setSixDigitCode] = useState("");
  const defaultFilters = ["payer", "location", "contract", "taxonomy"];
  const taxonomyProviderId = "4";
  const taxonomyOptionsByRole = useAppSelector(selectTaxonomyOptionsByRole);
  const profileUpdateStatus = useAppSelector(selectProfileUpdateStatus);
  const profileFilterRole = useAppSelector(selectProfileFilterRole);
  let filtersData: ProfileDefaultFilters = {};
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    message: "",
    variant: "",
  });
  let initialValues: ProfileFormValues = {
    firstName: user.name.split(" ")[0],
    lastName: user.name.split(" ")[1],
    phoneNumber: profile.phoneNumber ?? "",
    country: profile.country ?? "",
    state: profile.state ?? null,
    county: profile.county ?? null,
    city: profile.city ?? null,
    address: profile.address ?? "",
    postalCode: profile.postalCode ?? "",
    taxonomy: [] as SelectOption[],
    payer: [] as SelectOption[],
    location: [] as SelectOption[],
    contract: [] as SelectOption[],
  };

  initialValues.taxonomy = getSelectOptions(profile.taxonomy || []);

  const onEditClick = () => {
    setIsEditing((isEditing) => !isEditing);
  };

  const setup2FactorAuthentication = () => {
    setOpenModal(true);
    if (!isTwoFAEnabled) {
      dispatch(getOtpAuthUrl());
    }
  };

  const handleActivate2FA = async (otpToken: string) => {
    dispatch(verifyOtpAndActivate2FA(otpToken));
    handleCloseModal();
  };

  const handleDisable2FA = async () => {
    dispatch(disable2FA());
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSubmit = (data: any) => {
    let payload: any = {
      filterRoleId: profile.filterRoleId,
    };
    for (const key in data) {
      if (data[key] === null || data[key] === "") {
        payload[key] = null;
      } else {
        payload[key] = data[key];
      }
      if (
        defaultFilters.includes(key) &&
        data[key] !== undefined &&
        data[key] !== null &&
        data[key].length !== 0
      ) {
        payload[key] = Array.from(
          new Set(data[key].map((filter: any) => filter.label))
        );
      }
    }
    if (data["firstName"] !== "" || data["lastName"] !== "") {
      payload["name"] = `${data.firstName} ${data.lastName}`;
    }
    dispatch(updateProfile(user.id, payload));
    onEditClick();
  };

  useEffect(() => {
    dispatch(
      getFilterRolePermission(
        profile.filterRoleId,
        taxonomyProviderId,
        setTaxonomyOptionsByRoleRequestStatus,
        setTaxonomyOptionsByRole
      )
    );
  }, [dispatch, profile.filterRoleId]);

  useEffect(() => {
    dispatch(getProfileById(user.id));
  }, [dispatch, user.id]);

  filtersData["taxonomy" as keyof ProfileDefaultFilters] =
    taxonomyOptionsByRole;

  //  ===============================  LAUNCH SNACKBAR  ===============================

  useEffect(() => {
    if (profileUpdateStatus === Status.Success) {
      setToastMessage(ProfileToastMessages.editProfileSuccess);
    }
    if (profileUpdateStatus === Status.Failed) {
      setToastMessage(ProfileToastMessages.editProfileError);
    }
    if (twoFAUpdateStatus === Status.Success) {
      setToastMessage(ProfileToastMessages.Update2FASuccess);
    }
    if (twoFAUpdateStatus === Status.Failed) {
      setToastMessage(ProfileToastMessages.update2FAError);
    }
    if (toastMessage.message !== "") {
      setShowToast(true);
    }
  }, [profileUpdateStatus, twoFAUpdateStatus, toastMessage.message]);

  //  ===============================  2FA QR CODE GENERATION  ===============================
  useEffect(() => {
    if (otpAuthUrl) QRCode.toDataURL(otpAuthUrl).then(setQrCodeUrl);
  }, [otpAuthUrl]);

  // Handler to enable or disable the verify and activate btn
  useEffect(() => {
    setIsActionBtnDisabled(sixDigitCode?.length !== 6);
  }, [sixDigitCode?.length]);

  return (
    <Fragment>
      <Toast
        key="edit-profile"
        message={toastMessage.message}
        open={showToast}
        variant={toastMessage.variant}
        onClose={() => {
          dispatch(setProfileUpdateStatus(Status.Idle));
          dispatch(setTwoFAUpdateStatus(Status.Idle));
          setShowToast(false);
        }}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={EditProfileSchema}
      >
        {({ isValid, errors }) => {
          const enableToSubmit = () => Boolean(isValid);

          return (
            <Form>
              <Box className={styles.profileLayout}>
                <Box className={styles.profileRectangle}>
                  <Typography variant="h2" className={styles.profileTitle}>
                    My Profile
                  </Typography>
                  <Box
                    className={styles.profileUser}
                    display="flex"
                    justifyContent="space-between"
                  >
                    <Box display="flex" gap="20px">
                      <AccountCircle
                        className={styles.profilePicture}
                        color="primary"
                      />
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body1">{user.name}</Typography>
                        <Typography mt="4px" variant="caption">
                          {profileFilterRole.name}
                        </Typography>
                        <Typography color="#787878" mt="4px" variant="caption">
                          Dallas, TX
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" gap="10px" marginBottom="auto">
                      <Button
                        variant="outlined"
                        onClick={onEditClick}
                        type="button"
                      >
                        {isEditing ? "" : <Edit color={variables.mainBlue} />}
                        {isEditing ? "Cancel" : "Edit"}
                      </Button>
                      {/* 2 FACTOR AUTH */}
                      {!isEditing && (
                        <Button
                          variant="contained"
                          onClick={setup2FactorAuthentication}
                          type="button"
                        >
                          {isTwoFAEnabled ? "Disable 2FA" : "Setup 2FA"}
                        </Button>
                      )}
                      {isEditing && (
                        <Button
                          disabled={!enableToSubmit()}
                          variant="contained"
                          type="submit"
                        >
                          Save
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Grid container>
                    <ProfileCard
                      title="Personal Information"
                      isEditing={isEditing}
                      errors={errors}
                    />
                    <ProfileCard title="Address" isEditing={isEditing} />
                    <ProfileCard
                      title="Default Filters Configuration"
                      isEditing={isEditing}
                      filtersData={filtersData}
                    />
                  </Grid>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>

      {isTwoFAEnabled ? (
        <Modal
          actionLabel="Yes, disable"
          cancelActionLabel="Cancel"
          modalContent={
            <Fragment>
              <Box mt="1rem">
                <Typography variant="body1">
                  Are you sure you want to disable two-factor authentication?
                </Typography>
              </Box>
            </Fragment>
          }
          open={openModal}
          padding="3.5rem"
          title="Disable Two-factor Authentication (2FA)"
          width="700px"
          onSubmit={handleDisable2FA}
          onClose={handleCloseModal}
        />
      ) : (
        <Modal
          actionLabel="Verify & Activate"
          cancelActionLabel="Cancel"
          modalContent={
            <Fragment>
              <Box mt="1rem">
                <Typography variant="h3" color={variables.mainBlue} mb="1rem">
                  Configuring Google Authenticator or Authy
                </Typography>
                <Typography variant="body1">
                  1. Install Google Authenticator (IOS - Android) or Authy (IOS
                  - Android).
                </Typography>
                <Typography variant="body1">
                  2. In the authenticator app, select "+" icon.
                </Typography>
                <Typography variant="body1">
                  3. Select "Scan a barcode (or QR code)" and use the phone's
                  camera to scan this barcode.
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" mt="1rem">
                <Typography variant="h3" color={variables.mainBlue} mb="1rem">
                  Scan QR Code
                </Typography>
                {/* QR Code generated from url*/}
                <img
                  src={qrCodeUrl}
                  alt="qr code url"
                  width="256px"
                  style={{ alignSelf: "center" }}
                />
              </Box>
              <Box mt="1rem">
                <Typography variant="h3" color={variables.mainBlue} mb="1rem">
                  Verify 6-Digit Code
                </Typography>
                <Typography variant="body1">
                  For changing the setting, please verify the authentication
                  code:
                </Typography>
                <OutlinedInput
                  placeholder="Authentication code"
                  sx={{ marginTop: "1rem" }}
                  type="number"
                  onChange={(e) => setSixDigitCode(e.target.value)}
                />
              </Box>
            </Fragment>
          }
          isActionBtnDisabled={isActionBtnDisabled}
          padding="2rem"
          title="Two-factor Authentication (2FA)"
          open={openModal}
          onClose={handleCloseModal}
          onSubmit={() => {
            if (sixDigitCode) handleActivate2FA(sixDigitCode);
          }}
        />
      )}
    </Fragment>
  );
};

export default MyProfile;
